import { Link, useNavigate } from '@tanstack/react-router';
import type { Build } from '@/api/schemas';
import { formatDateTime, formatDuration, shortSha, timeAgo } from '@/lib/format';
import { stagger } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { useWording } from '@/lib/useAudience';
import { Icon } from '@/components/Icon';
import { EnvPill, PlatformIcon, Tag } from '@/components/badges';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { STATUS_LABEL_SHORT, buildDuration, requesterOf } from './buildHelpers';
import { statusLabel } from './buildLabels';
import { BuildStatusPill } from './BuildStatusPill';
import { buildNumberPrefix, isHotfixBranch } from './buildsListModel';

type Requester = ReturnType<typeof requesterOf>;

/**
 * Builds table. Two densities in one table (CSS only, same rows and testids):
 * - ≥ 1440 (xl): seven columns — build, status, platform · env, version, commit, duration, requested (screen 02 desktop);
 * - 641–1439: four stacked columns like the tablet mockup — build & commit, status & timing, platform & profile, version.
 *   Below 1440 the desktop sidebar or the icon rail leaves 700–1150 px, too little for seven columns without spills.
 * - table narrower than 600 px (iPad split view, phones in landscape): three columns; the version moves under the
 *   platform. Decided by a container query on the table's wrapper, not by the viewport.
 */
export function BuildsTable({ builds, now, projectSlug }: { builds: Build[]; now: number; projectSlug: string }) {
  const navigate = useNavigate();
  const open = (buildId: string) => void navigate({ to: '/projects/$slug/builds/$buildId', params: { slug: projectSlug, buildId } });
  return (
    <Table
      aria-label="Builds"
      className="table-fixed sm:[&_td]:px-3 sm:[&_th]:px-3 sm:[&_td:first-child]:pl-5 sm:[&_th:first-child]:pl-5 sm:[&_td:last-child]:pr-5 sm:[&_th:last-child]:pr-5"
    >
      <BuildsTableHead />
      <TableBody>
        {builds.map((b, i) => (
          <BuildRow key={b.id} b={b} index={i} now={now} projectSlug={projectSlug} onOpen={open} />
        ))}
      </TableBody>
    </Table>
  );
}

function BuildsTableHead() {
  return (
    <TableHeader>
      <TableRow className="hover:bg-transparent">
        <TableHead className="xl:w-[17%]">
          <span className="xl:hidden">Build &amp; commit</span>
          <span className="max-xl:hidden">Build</span>
        </TableHead>
        <TableHead className="w-[26%] xl:w-[14%] @max-[37.5rem]:w-[28%]">Status</TableHead>
        <TableHead className="w-[22%] xl:w-[14%] @max-[37.5rem]:w-[30%]">Platform · env</TableHead>
        <TableHead className="w-[12%] xl:w-[8%] @max-[37.5rem]:hidden">Version</TableHead>
        <TableHead className="max-xl:hidden">Commit</TableHead>
        <TableHead className="w-[8%] max-xl:hidden">Duration</TableHead>
        <TableHead className="w-[16%] max-xl:hidden">Requested</TableHead>
      </TableRow>
    </TableHeader>
  );
}

const rowClass = (b: Build) => cn('enter lift-row cursor-pointer', b.environment.kind === 'production' && 'border-l-2 border-l-env-production', b.status === 'canceled' && 'opacity-70');

function BuildRow({ b, index, now, projectSlug, onOpen }: { b: Build; index: number; now: number; projectSlug: string; onOpen: (buildId: string) => void }) {
  const req = requesterOf(b);
  const duration = buildDuration(b, now);
  const profile = b.profile.name ?? b.profile.slug;
  return (
    <TableRow
      data-testid={`build-row-${b.ref}`}
      data-status={b.status}
      style={stagger(Math.min(index, 10))}
      className={rowClass(b)}
      onClick={() => onOpen(b.id)}
    >
      <BuildCell b={b} profile={profile} projectSlug={projectSlug} />
      <StatusCell b={b} duration={duration} req={req} now={now} />
      <PlatformCell b={b} profile={profile} />
      <VersionCell b={b} />
      <TableCell className="whitespace-normal py-2.5 max-xl:hidden">
        <CommitCell b={b} />
      </TableCell>
      <TableCell className="tabular py-2.5 font-mono text-xs text-muted-foreground max-xl:hidden">{formatDuration(duration)}</TableCell>
      <RequestedCell b={b} req={req} now={now} />
    </TableRow>
  );
}

function BuildCell({ b, profile, projectSlug }: { b: Build; profile: string; projectSlug: string }) {
  return (
    <TableCell className="overflow-hidden py-2.5">
      <Link to="/projects/$slug/builds/$buildId" params={{ slug: projectSlug, buildId: b.id }} className="block min-w-0 rounded-sm" onClick={(e) => e.stopPropagation()}>
        <span className="flex min-w-0 font-mono text-[13px] font-medium text-foreground">
          <span className="truncate">{b.app.slug}</span>
          <span className="shrink-0 text-subtle">#{b.number}</span>
        </span>
        <span className="block truncate text-xs text-subtle max-xl:hidden">{profile}</span>
      </Link>
      <div className="mt-1 xl:hidden">
        <CommitCell b={b} />
      </div>
    </TableCell>
  );
}

function StatusCell({ b, duration, req, now }: { b: Build; duration: number | null; req: Requester; now: number }) {
  return (
    <TableCell className="overflow-hidden py-2.5">
      <div className="flex min-w-0 flex-col items-start gap-1">
        <BuildStatusPill
          status={b.status}
          label={
            <>
              <span className="max-lg:hidden">{statusLabel(b.status)}</span>
              <span className="lg:hidden">{STATUS_LABEL_SHORT[b.status]}</span>
            </>
          }
        />
        {b.status === 'queued' && b.priority !== 'normal' ? <Tag className="text-awaiting">{b.priority}</Tag> : null}
        <span className="tabular block max-w-full truncate font-mono text-[11px] text-subtle xl:hidden">
          {formatDuration(duration)} · <time dateTime={b.createdAt} title={formatDateTime(b.createdAt)}>{timeAgo(b.createdAt, now)}</time>
        </span>
        <span className="flex max-w-full items-center gap-1 text-[11px] text-subtle xl:hidden" title={req.label}>
          <Icon name={req.icon} size={13} className="shrink-0" />
          <span className="truncate">{req.label}</span>
        </span>
      </div>
    </TableCell>
  );
}

function PlatformCell({ b, profile }: { b: Build; profile: string }) {
  return (
    <TableCell className="overflow-hidden py-2.5">
      <div className="flex min-w-0 items-center gap-2">
        <PlatformIcon platform={b.platform} />
        <EnvPill env={b.environment} className="min-w-0" />
      </div>
      <span className="mt-1 block truncate font-mono text-xs text-subtle xl:hidden">{profile}</span>
      <span className="tabular hidden truncate font-mono text-xs text-muted-foreground @max-[37.5rem]:block">
        {b.versionName ?? '—'}
        {b.buildNumber ? <span className="text-subtle"> · {buildNumberPrefix(b.platform)} {b.buildNumber}</span> : null}
      </span>
    </TableCell>
  );
}

function VersionCell({ b }: { b: Build }) {
  return (
    <TableCell className="tabular overflow-hidden py-2.5 font-mono text-xs text-muted-foreground @max-[37.5rem]:hidden">
      <span className="block truncate" title={b.versionName ?? undefined}>{b.versionName ?? '—'}</span>
      {b.buildNumber ? <span className="block truncate text-subtle">{buildNumberPrefix(b.platform)} {b.buildNumber}</span> : null}
    </TableCell>
  );
}

function RequestedCell({ b, req, now }: { b: Build; req: Requester; now: number }) {
  return (
    <TableCell className="py-2.5 max-xl:hidden">
      <div className="flex min-w-0 items-center gap-2">
        <Icon name={req.icon} size={16} className="shrink-0 text-subtle" />
        <div className="min-w-0">
          <div className="truncate text-xs text-foreground" title={req.label}>
            {req.label}
          </div>
          <time dateTime={b.createdAt} title={formatDateTime(b.createdAt)} className="tabular block truncate font-mono text-[11px] text-subtle">
            {timeAgo(b.createdAt, now)}
          </time>
        </div>
      </div>
    </TableCell>
  );
}

export function CommitCell({ b }: { b: Build }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex min-w-0 items-center gap-2">
        <Tag className="text-foreground">{shortSha(b.git.commitSha) || '—'}</Tag>
        <span className={cn('truncate font-mono text-xs', isHotfixBranch(b.git.branch) ? 'text-failed' : 'text-muted-foreground')}>{b.git.branch ?? '—'}</span>
      </div>
      {b.error ? <CommitError error={b.error} /> : <CommitMessage message={b.git.commitMessage} />}
    </div>
  );
}

function CommitError({ error }: { error: NonNullable<Build['error']> }) {
  const say = useWording();
  return (
    <span className="flex max-w-full items-center gap-1 rounded-md border border-failed/30 bg-failed/10 px-1.5 py-0.5 font-mono text-[11px] text-failed" title={`${error.code} · ${say(error.message)}`}>
      <Icon name="error" size={13} className="shrink-0" />
      <span className="truncate">{say(error.message)}</span>
    </span>
  );
}

function CommitMessage({ message }: { message: string | null | undefined }) {
  if (!message) return null;
  return (
    <span className="truncate text-[13px] text-muted-foreground" title={message}>
      {message}
    </span>
  );
}
